import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useIsMobile } from "../../hooks/use-mobile";
import { ArrowLeft, Lock } from "lucide-react";
import { useReportIds } from "../../hooks/useReportIds";
import Loading from "../Loading";
import { motion } from "framer-motion";
import { Badge } from "../ui/badge";
import { useTranslation } from "react-i18next";

const ActionPlanReport = () => {
  const { t, i18n } = useTranslation();
  const { reportIds, isLoading } = useReportIds();
  const { reportId } = useParams();
  const location = useLocation();
  const [currentReportId, setCurrentReportId] = useState<string | null>(reportId || null);
  const [reportData, setReportData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  
  // Check if we're on the start-report path (no specific report ID)
  const isStartReportPath = location.pathname.includes('/start-report/');

  useEffect(() => {
    if (isStartReportPath) {
      // For start-report path, use the INTAKE3 report ID if available
      if (reportIds.INTAKE3) {
        setCurrentReportId(reportIds.INTAKE3 as string);
      }
    } else if (!reportId && reportIds.INTAKE3) {
      // For other paths, use the reportId from params or fall back to INTAKE3
      setCurrentReportId(reportIds.INTAKE3 as string);
    } else if (reportId) {
      // If reportId is in URL, use it
      setCurrentReportId(reportId);
    }
  }, [reportId, reportIds?.INTAKE3, isStartReportPath]);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!currentReportId) return;
      try {
        setLoading(true);
        const response = await fetch(`${(import.meta as any).env.VITE_APP_URL}/report/intake/3/${currentReportId}`, {
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.status === 500) {
          setError({ status: 500 });
          setLoading(false);
          return;
        }

        if (!response.ok) {
          setError({ status: response.status });
          setLoading(false);
          return;
        }

        const data = await response.json();
        setReportData(data);
        setError(null);
      } catch (err: any) {
        setError({ status: 'error', message: err.message });
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [currentReportId]);

  if (isLoading || loading) {
    return <Loading className="h-screen" />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{t("intakeReport.error.loading", "Error Loading Report")}</h1>
          <p className="text-gray-600 mb-6">
            {error.status === 404
              ? t("intakeReport.error.notFound", "Report not found. It may have been deleted or you don't have access.")
              : error.status === 500
              ? t("intakeReport.error.server", "Server error. Please try again later.")
              : t("intakeReport.error.unknown", "Error: {{message}}", { message: error.message || "Unknown error" })}
          </p>
          <Link to="/">
            <Button>{t("common.backToHome", "Return to Dashboard")}</Button>
          </Link>
        </div>
      </div>
    );
  }
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'it' ? 'it-IT' : 'uk-UA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper function to format area titles
  const formatAreaTitle = (key: string): string => {
    switch (key) {
      case "sleep":
        return t("intakeReport.functionalAreas.sleep");
      case "socialSupport":
        return t("intakeReport.functionalAreas.socialSupport");
      case "energyLevel":
        return t("intakeReport.functionalAreas.energyLevel");
      case "stressManagement":
        return t("intakeReport.functionalAreas.stressManagement");
      case "cognitiveFunction":
        return t("intakeReport.functionalAreas.cognitiveFunction");
      case "emotionalRegulation":
        return t("intakeReport.functionalAreas.emotionalRegulation");
      default:
        return key.charAt(0).toUpperCase() + key.slice(1);
    }
  };

  // Define animations for card sections
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6">
      <div className="container max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
          <Link to="/">
            <Button variant="ghost" className="-ml-2 pl-2 flex items-center gap-2 text-base">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t("intakeProcess.backToIntake", "Back to Intake Process")}</span>
              <span className="sm:hidden">{t("navigation.back")}</span>
            </Button>
          </Link>
        </div>
        {/* Header */}
        <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold">{t("intakeProcess.session.actionPlanDevelopment.title")}</h1>
            <p className="mt-2">{formatDate(reportData?.userInfo?.date)} • {t("intakeProcess.session.number", "Session {{number}}", { number: reportData?.userInfo?.callNumber })}</p>
          <p className="text-gray-600">
            {reportData?.userInfo?.name} • {reportData?.userInfo?.email}
          </p>
        </div>

        {/* Welcome Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          className="mb-8 text-center"
        >
          <h2 className="text-2xl font-bold mb-2">{reportData?.welcome?.greeting}</h2>
          <p className="text-gray-600">{reportData?.welcome?.message}</p>
        </motion.div>

        {/* Patterns Addressed Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8"
        >
          <div className="bg-[#006C5B] text-white p-4 rounded-md mb-4">
            <h2 className="text-xl font-medium">{t("intakeReport.sections.patternsAddressed", "Patterns Addressed")}</h2>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            {reportData?.patternsAddressed?.map((item, index) => (
              <Card key={index} className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{item?.pattern}</CardTitle>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-700">{item?.approachDeveloped}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Client Goals Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-8"
        >
          <div className="bg-[#006C5B] text-white p-4 rounded-md mb-4">
            <h2 className="text-xl font-medium">{t("intakeReport.sections.clientGoals", "Client Goals")}</h2>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            {reportData?.clientGoals?.map((goal, index) => (
              <Card key={index} className="shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <p className="mb-4 text-gray-700">{goal?.goalStatement}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {goal.relatedAreas.map((area, i) => (
                      <Badge key={i} variant="secondary" className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                        {formatAreaTitle(area)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Recommendations Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-8"
        >
          <div className="bg-[#006C5B] text-white p-4 rounded-md mb-4">
            <h2 className="text-xl font-medium">{t("intakeReport.sections.recommendations")}</h2>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            {reportData?.recommendations?.map((recommendation, index) => (
              <Card key={index} className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <CardTitle className="text-lg">{recommendation.title}</CardTitle>
                    <Badge className={`${recommendation.priority === 'High' ? 'bg-red-500' : 'bg-orange-400'} hover:bg-none`}>
                      {t(`intakeReport.priority.${recommendation?.priority}`)} {t("intakeReport.details.priority")}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <p className="mb-4 text-gray-700">{recommendation?.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-2">{t("intakeReport.sections.steps", "Steps")}:</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {recommendation?.steps.map((step, i) => (
                        <li key={i} className="text-gray-700">{step}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">{t("intakeReport.details.relatedAreas")}:</h4>
                    <div className="flex flex-wrap gap-2">
                      {recommendation?.relatedAreas.map((area, i) => (
                        <Badge key={i} variant="outline" className="bg-gray-50">
                          {formatAreaTitle(area)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Potential Challenges Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mb-8"
        >
          <div className="bg-[#006C5B] text-white p-4 rounded-md mb-4">
            <h2 className="text-xl font-medium">{t("intakeReport.sections.potentialChallenges", "Potential Challenges")}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportData?.potentialChallenges?.map((item, index) => (
              <Card key={index} className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-red-600">{t("intakeReport.sections.challenge", "Challenge")}</CardTitle>
                </CardHeader>
                
                <CardContent>
                  <p className="mb-4 text-gray-700">{item.challenge}</p>
                  
                  <div className="pl-4 border-l-2 border-green-500">
                    <h4 className="font-medium text-green-700 mb-1">{t("intakeReport.sections.strategy", "Strategy")}:</h4>
                    <p className="text-gray-700">{item?.mitigationStrategy}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Follow-Up Plan Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="bg-[#006C5B] text-white p-4 rounded-md mb-4">
            <h2 className="text-xl font-medium">{t("intakeReport.sections.followUpPlan", "Follow-Up Plan")}</h2>
          </div>
          
          <Card className="shadow-sm">
            <CardContent className="pt-6">
              <div className="mb-4">
                  <span className="font-medium">{t("intakeReport.sections.nextContactDate", "Next Contact Date")}:</span> {reportData?.followUpPlan?.nextContactDate}
              </div>
              
              <h4 className="font-medium text-gray-700 mb-2">{t("intakeReport.sections.focusAreas", "Focus Areas")}:</h4>
              <ul className="list-disc pl-5 space-y-2">
                {reportData?.followUpPlan?.followUpFocus?.map((item, index) => (
                  <li key={index} className="text-gray-700">{item}</li>
                ))}
              </ul>
              
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto">{t("intakeProcess.backToIntake")}</Button>
                </Link>
                <Link to="/auth/dashboard" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto">{t("navigation.scheduleFollowup")}</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.section>
      </div>
    </div>
  );
};

export default ActionPlanReport;
